(function () {
  if (customElements.get('product-multi-purchase')) return;

  class ProductMultiPurchase extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.openButton = this.querySelector('[data-mp-open]');
      this.modal = this.querySelector('[data-mp-modal]');
      this.addButtons = this.querySelectorAll('[data-mp-add]');
      this.message = this.querySelector('[data-mp-message]');
      this.activeCellPicker = null;
      this.storageKey = this.buildStorageKey();

      this.onClick = this.handleClick.bind(this);
      this.onInput = this.handleInput.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onFocusIn = this.handleFocusIn.bind(this);
      this.onPopState = this.handlePopState.bind(this);

      this.addEventListener('click', this.onClick, true);
      this.addEventListener('input', this.onInput);
      this.addEventListener('change', this.onInput);
      this.addEventListener('focusin', this.onFocusIn);
      document.addEventListener('keydown', this.onKeydown, true);
      window.addEventListener('popstate', this.onPopState, true);
      this.restoreQuantities();
      this.updateSummary();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick, true);
      this.removeEventListener('input', this.onInput);
      this.removeEventListener('change', this.onInput);
      this.removeEventListener('focusin', this.onFocusIn);
      document.removeEventListener('keydown', this.onKeydown, true);
      window.removeEventListener('popstate', this.onPopState, true);
    }

    get inputs() {
      return Array.from(this.querySelectorAll('[data-mp-input]'));
    }

    isMobileLayout() {
      return window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
    }

    buildStorageKey() {
      const productTitle = this.dataset.productTitle || 'product';
      return `multi-purchase:${window.location.pathname}:${productTitle}`;
    }

    restoreQuantities() {
      let saved;
      try {
        saved = JSON.parse(sessionStorage.getItem(this.storageKey) || '{}');
      } catch (error) {
        saved = {};
      }
      this.inputs.forEach((input) => {
        if (!Object.prototype.hasOwnProperty.call(saved, input.dataset.variantId)) return;
        const quantity = this.normaliseQuantity(saved[input.dataset.variantId], input);
        input.value = quantity > 0 ? quantity : (input.matches('[data-mp-matrix-input]') ? '' : 0);
      });
    }

    persistQuantities() {
      const values = {};
      this.inputs.forEach((input) => {
        const quantity = this.normaliseQuantity(input.value, input);
        if (quantity > 0) values[input.dataset.variantId] = quantity;
      });

      try {
        if (Object.keys(values).length) {
          sessionStorage.setItem(this.storageKey, JSON.stringify(values));
        } else {
          sessionStorage.removeItem(this.storageKey);
        }
      } catch (error) {
        // Ignore storage failures; the live inputs still hold the values.
      }
    }

    handleClick(event) {
      const open = event.target.closest('[data-mp-open]');
      const close = event.target.closest('[data-mp-close]');
      const minus = event.target.closest('[data-mp-minus]');
      const plus = event.target.closest('[data-mp-plus]');
      const clear = event.target.closest('[data-mp-clear]');
      const add = event.target.closest('[data-mp-add]');
      const matrixInput = event.target.closest('[data-mp-matrix-input]');
      const cellQuantity = event.target.closest('[data-mp-cell-quantity]');
      const mobileDiameter = event.target.closest('[data-mp-mobile-diameter]');
      const mobileCell = event.target.closest('[data-mp-mobile-cell]');
      const mobileCellQuantity = event.target.closest('[data-mp-mobile-cell-quantity]');
      const mobileMinus = event.target.closest('[data-mp-mobile-minus]');
      const mobilePlus = event.target.closest('[data-mp-mobile-plus]');
      const remove = event.target.closest('[data-mp-remove]');

      if (open) {
        event.preventDefault();
        this.openModal();
      } else if (close) {
        event.preventDefault();
        this.closeModal();
      } else if (cellQuantity) {
        event.preventDefault();
        const cell = cellQuantity.closest('.multi-purchase-matrix__cell');
        const input = cell && cell.querySelector('[data-mp-matrix-input]');
        if (!input) return;
        input.value = this.normaliseQuantity(cellQuantity.dataset.mpCellQuantity, input);
        this.updateSummary();
        this.closeCellPicker();
      } else if (mobileCellQuantity) {
        event.preventDefault();
        const picker = mobileCellQuantity.closest('[data-mp-mobile-cell-picker]');
        const cell = picker && picker.closest('.multi-purchase-mobile-cell');
        const target = cell && this.mobileTargetInput(cell);
        if (!target) return;
        target.value = this.normaliseQuantity(mobileCellQuantity.dataset.mpMobileCellQuantity, target);
        this.updateSummary();
        this.closeMobileCellPicker();
      } else if (mobileDiameter) {
        event.preventDefault();
        this.setMobileDiameter(mobileDiameter.dataset.mpMobileDiameter);
      } else if (mobileMinus || mobilePlus) {
        event.preventDefault();
        this.adjustMobileQuantity(mobileMinus || mobilePlus, Boolean(mobilePlus));
      } else if (mobileCell) {
        event.preventDefault();
        this.adjustMobileQuantity(mobileCell, true);
      } else if (matrixInput) {
        if (this.isMobileLayout()) return;
        this.openCellPicker(matrixInput);
      } else if (minus || plus) {
        event.preventDefault();
        const wrapper = (minus || plus).closest('[data-mp-quantity]');
        const input = wrapper && wrapper.querySelector('[data-mp-input]');
        if (!input) return;
        const current = this.normaliseQuantity(input.value, input);
        input.value = plus ? this.normaliseQuantity(current + 1, input) : Math.max(0, current - 1);
        this.updateSummary();
      } else if (clear) {
        event.preventDefault();
        this.inputs.forEach((input) => { input.value = input.matches('[data-mp-matrix-input]') ? '' : 0; });
        this.closeCellPicker();
        this.closeMobileCellPicker();
        this.setMessage('');
        this.updateSummary();
      } else if (remove) {
        event.preventDefault();
        const input = this.inputs.find((candidate) => candidate.dataset.variantId === remove.dataset.mpRemove);
        if (!input) return;
        input.value = input.matches('[data-mp-matrix-input]') ? '' : 0;
        this.updateSummary();
      } else if (add) {
        event.preventDefault();
        this.closeCellPicker();
        this.closeMobileCellPicker();
        this.addSelected(add);
      }
    }

    handleInput(event) {
      if (!event.target.matches('[data-mp-input]')) return;
      if (event.target.value === '') {
        this.updateSummary();
        return;
      }
      event.target.value = this.normaliseQuantity(event.target.value, event.target);
      this.updateSummary();
    }

    handleFocusIn(event) {
      if (event.target.matches('[data-mp-matrix-input]') && !this.isMobileLayout()) this.openCellPicker(event.target);
    }

    handlePopState(event) {
      if (!this.modal || !this.modal.classList.contains('is-open') || !this.modalHistoryActive) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.closeModal({ fromHistory: true });
    }

    openCellPicker(input) {
      const cell = input.closest('.multi-purchase-matrix__cell');
      const picker = cell && cell.querySelector('[data-mp-cell-picker]');
      if (!picker || picker === this.activeCellPicker) return;
      this.closeCellPicker();
      picker.hidden = false;
      cell.classList.add('is-picker-open');
      this.activeCellPicker = picker;
    }

    closeCellPicker() {
      if (!this.activeCellPicker) return;
      this.activeCellPicker.hidden = true;
      const cell = this.activeCellPicker.closest('.multi-purchase-matrix__cell');
      if (cell) cell.classList.remove('is-picker-open');
      this.activeCellPicker = null;
    }

    mobileTargetInput(cell) {
      const trigger = cell && cell.querySelector('[data-mp-mobile-cell]');
      const targetId = trigger && trigger.dataset.mpTarget;
      if (!targetId) return null;
      return Array.from(this.querySelectorAll('[data-mp-matrix-input]')).find((input) => input.id === targetId) || null;
    }

    setMobileDiameter(value) {
      const tabs = Array.from(this.querySelectorAll('[data-mp-mobile-diameter]'));
      const columns = Array.from(this.querySelectorAll('[data-mp-mobile-column]'));
      const cells = Array.from(this.querySelectorAll('[data-mp-mobile-matrix-cell]'));
      const selectedValue = value === 'all' ? (columns[0] && columns[0].dataset.mpMobileColumn) : value;

      tabs.forEach((tab) => {
        const isActive = tab.dataset.mpMobileDiameter === value;
        const isSelected = tab.dataset.mpMobileDiameter === selectedValue;
        tab.classList.toggle('is-active', isActive);
        tab.classList.toggle('is-selected', isSelected);
        tab.setAttribute('aria-selected', String(isActive));
      });

      columns.forEach((column) => {
        column.classList.toggle('is-active', column.dataset.mpMobileColumn === selectedValue);
      });
      cells.forEach((cell) => {
        cell.classList.toggle('is-active', cell.dataset.mpMobileMatrixCell === selectedValue);
      });

      const activeColumn = columns.find((column) => column.dataset.mpMobileColumn === selectedValue);
      if (activeColumn) activeColumn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      this.closeMobileCellPicker();
    }

    adjustMobileQuantity(control, shouldIncrease) {
      const targetId = control && control.dataset.mpTarget;
      const input = targetId ? this.inputs.find((candidate) => candidate.id === targetId) : null;
      if (!input) return;
      const current = this.normaliseQuantity(input.value, input);
      const next = shouldIncrease ? this.normaliseQuantity(current + 1, input) : Math.max(0, current - 1);
      input.value = next > 0 ? next : '';
      this.updateSummary();
    }

    openMobileCellPicker(button) {
      const cell = button.closest('.multi-purchase-mobile-cell');
      const picker = cell && cell.querySelector('[data-mp-mobile-cell-picker]');
      if (!picker || picker === this.activeMobileCellPicker) return;
      this.closeCellPicker();
      this.closeMobileCellPicker();
      picker.hidden = false;
      cell.classList.add('is-picker-open');
      this.activeMobileCellPicker = picker;
    }

    closeMobileCellPicker() {
      if (!this.activeMobileCellPicker) return;
      this.activeMobileCellPicker.hidden = true;
      const cell = this.activeMobileCellPicker.closest('.multi-purchase-mobile-cell');
      if (cell) cell.classList.remove('is-picker-open');
      this.activeMobileCellPicker = null;
    }

    handleKeydown(event) {
      if (!this.modal || !this.modal.classList.contains('is-open')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.activeCellPicker) {
          this.closeCellPicker();
          return;
        }
        if (this.activeMobileCellPicker) {
          this.closeMobileCellPicker();
          return;
        }
        this.closeModal();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = Array.from(this.modal.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'))
        .filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    normaliseQuantity(value, input) {
      let quantity = Math.max(0, parseInt(value, 10) || 0);
      const maximum = parseInt(input && input.max, 10);
      if (!Number.isNaN(maximum)) quantity = Math.min(quantity, maximum);
      return quantity;
    }

    selectedItems() {
      return this.inputs.map((input) => ({
        id: Number(input.dataset.variantId),
        quantity: this.normaliseQuantity(input.value, input),
        price: Number(input.dataset.price) || 0,
        title: input.dataset.variantTitle || input.getAttribute('aria-label') || ''
      })).filter((item) => item.quantity > 0);
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    renderSummaryItem(item) {
      const parts = item.title.split('/').map((part) => part.trim()).filter(Boolean);
      const title = parts.slice(0, 2).join(' / ') || item.title;
      const mobileTitle = parts.length >= 2 ? `${parts[1]} x ${parts[0]}` : title;
      const meta = parts.slice(2).join(' / ');

      return `
        <div class="multi-purchase-summary-item">
          <span class="multi-purchase-summary-item__qty">${item.quantity}x</span>
          <div class="multi-purchase-summary-item__content">
            <div class="multi-purchase-summary-item__title">
              <span class="multi-purchase-summary-item__title-desktop">${this.escapeHtml(title)}</span>
              <span class="multi-purchase-summary-item__title-mobile">${this.escapeHtml(mobileTitle)}</span>
            </div>
            ${meta ? `<div class="multi-purchase-summary-item__meta">${this.escapeHtml(meta)}</div>` : ''}
          </div>
          <button class="multi-purchase-summary-item__remove" type="button" data-mp-remove="${item.id}" aria-label="Remove ${this.escapeHtml(title)}">×</button>
        </div>
      `;
    }

    updateSummary() {
      const items = this.selectedItems();
      const count = items.reduce((sum, item) => sum + item.quantity, 0);
      const total = items.reduce((sum, item) => sum + item.quantity * item.price, 0);
      this.persistQuantities();

      this.querySelectorAll('[data-mp-count]').forEach((element) => { element.textContent = count; });
      this.querySelectorAll('[data-mp-total]').forEach((element) => { element.innerHTML = this.formatMoney(total); });
      this.querySelectorAll('[data-mp-summary-list]').forEach((element) => {
        if (!items.length) {
          element.textContent = 'No selections yet.';
          element.classList.add('is-empty');
          return;
        }

        element.innerHTML = `<div class="multi-purchase-summary-list">${items.map((item) => this.renderSummaryItem(item)).join('')}</div>`;
        element.classList.remove('is-empty');
      });
      this.addButtons.forEach((button) => { button.disabled = count === 0; });

      this.inputs.forEach((input) => {
        const cell = input.closest('.multi-purchase-matrix__cell');
        if (!cell) return;
        const quantity = this.normaliseQuantity(input.value, input);
        cell.classList.toggle('has-quantity', quantity > 0);
      });

      this.querySelectorAll('[data-mp-mobile-cell]').forEach((button) => {
        const target = this.mobileTargetInput(button.closest('.multi-purchase-mobile-cell'));
        const quantity = target ? this.normaliseQuantity(target.value, target) : 0;
        const value = button.querySelector('[data-mp-mobile-value]');
        if (value) value.textContent = quantity;
        button.classList.toggle('has-quantity', quantity > 0);
        const wrapper = button.closest('[data-mp-mobile-quantity]');
        if (wrapper) wrapper.classList.toggle('has-quantity', quantity > 0);
      });
    }

    formatMoney(cents) {
      if (window.theme && theme.Shopify && typeof theme.Shopify.formatMoney === 'function') {
        return theme.Shopify.formatMoney(cents, theme.money_format_with_product_code_preference || theme.money_format);
      }
      const currency = window.Shopify && Shopify.currency ? Shopify.currency.active : 'USD';
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
    }

    openModal() {
      if (!this.modal) return;
      this.updateSummary();
      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('multi-purchase-modal-open');
      if (!this.modalHistoryActive && window.history && history.pushState) {
        try {
          const currentState = history.state && typeof history.state === 'object' ? history.state : {};
          history.replaceState({ ...currentState, multiPurchaseModalBase: true }, document.title, window.location.href);
          history.pushState({ ...currentState, multiPurchaseModal: true }, document.title, window.location.href);
          this.modalHistoryActive = true;
        } catch (error) {
          this.modalHistoryActive = false;
        }
      }
      const close = this.modal.querySelector('[data-mp-close]:not(.multi-purchase-modal__backdrop)');
      if (close) close.focus();
    }

    closeModal(options = {}) {
      if (!this.modal) return;
      if (document.activeElement && document.activeElement.matches('[data-mp-input]')) {
        document.activeElement.blur();
      }
      this.updateSummary();
      this.closeCellPicker();
      this.closeMobileCellPicker();
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('multi-purchase-modal-open');
      if (this.modalHistoryActive && !options.fromHistory && window.history && history.replaceState) {
        try {
          const currentState = history.state && typeof history.state === 'object' ? history.state : {};
          const { multiPurchaseModal, ...nextState } = currentState;
          history.replaceState(nextState, document.title, window.location.href);
        } catch (error) {
          // Closing the modal should never force navigation.
        }
      }
      this.modalHistoryActive = false;
      if (this.openButton) this.openButton.focus();
    }

    async addSelected(button) {
      const selected = this.selectedItems();
      if (!selected.length || button.disabled) return;

      const originalHtml = button.innerHTML;
      button.disabled = true;
      this.setMessage('');

      try {
        const response = await fetch((window.theme && theme.routes && theme.routes.cart_add_url) || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: selected.map(({ id, quantity }) => ({ id, quantity })) })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Unable to add the selected products.');

        this.inputs.forEach((input) => { input.value = input.matches('[data-mp-matrix-input]') ? '' : 0; });
        this.updateSummary();
        this.persistQuantities();
        this.setMessage('Selected products were added to your cart.', 'success');

        if (window.theme && theme.settings && theme.settings.after_add_to_cart === 'page') {
          window.location.href = theme.routes.cart_url;
          return;
        }

        document.documentElement.dispatchEvent(new CustomEvent('theme:cartchanged', {
          bubbles: true,
          detail: { openDrawer: Boolean(window.theme && theme.settings && theme.settings.after_add_to_cart === 'drawer') }
        }));

        this.closeModal();
      } catch (error) {
        this.setMessage(error.message || 'Unable to add the selected products.', 'error');
        button.innerHTML = originalHtml;
      } finally {
        button.disabled = this.selectedItems().length === 0;
      }
    }

    setMessage(text, type) {
      if (!this.message) return;
      this.message.textContent = text || '';
      this.message.classList.toggle('is-success', type === 'success');
      this.message.classList.toggle('is-error', type === 'error');
    }
  }

  customElements.define('product-multi-purchase', ProductMultiPurchase);
})();
